import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { usePageMeta } from "../lib/pageMeta";

/**
 * Cierge Labs Journey Storybook —
 *   Governed Agentic Procurement
 *   An illustrative circular-asset reference journey
 *
 * This is an independent KATLAS reference architecture. It is NOT a Shopify,
 * Google or UCP partnership, integration, certification, endorsement or live
 * protocol implementation. The page must surface this clearly near the top.
 *
 * No live merchant integration, no live protocol implementation, no live
 * KATLAS-signed transaction. The badge is `Illustrative reference architecture`.
 */

const architectureLayers = [
  {
    n: "01",
    title: "Machine-readable commerce layer",
    body:
      "A UCP-compatible merchant or marketplace makes product, price, stock, checkout and fulfilment capabilities intelligible to the buyer agent. This page references the Universal Commerce Protocol descriptively only; nothing here is a UCP integration or endorsement.",
  },
  {
    n: "02",
    title: "Circular-asset evidence layer",
    body:
      "The supplier or asset-passport custodian retains repair records, conformity documents, warranty details, provenance information and other commercially sensitive evidence. Only bounded claims or verifiable references are shared where needed.",
  },
  {
    n: "03",
    title: "KATLAS governed operational layer",
    body:
      "KATLAS applies the procurement authority envelope: buyer role and organisation, project or maintenance purpose, asset class and technical conditions, approved-supplier requirement, delegated budget limit, required evidence conditions, technical-review requirement, exception and escalation route, authority expiry or revocation condition.",
  },
  {
    n: "04",
    title: "Governed action and receipt",
    body:
      "Once the relevant conditions are met, KATLAS records a receipt for the procurement decision. The receipt may show: action outcome (approved, refused, escalated or pending evidence), authority basis and policy reference, asset and supplier references, shared evidence references, withheld evidence count, approving roles, timestamp and verification status. No raw maintenance file, private commercial terms, full technical document, personal data or proprietary supplier record is exposed publicly.",
  },
];

const journey = [
  {
    n: "01",
    title: "Discover",
    body: "Buyer agent finds an appropriate refurbished asset through a machine-readable commerce interface.",
  },
  {
    n: "02",
    title: "Qualify",
    body: "Supplier provides bounded product, availability, warranty and asset-passport assertions.",
  },
  {
    n: "03",
    title: "Check authority",
    body: "KATLAS checks buyer role, project purpose, budget, policy and supplier approval conditions.",
  },
  {
    n: "04",
    title: "Review evidence",
    body: "Technical reviewer confirms the required evidence threshold without unnecessarily copying sensitive material.",
  },
  {
    n: "05",
    title: "Commit or escalate",
    body: "The purchase proceeds, requires a further approval or is refused.",
  },
  {
    n: "06",
    title: "Accept and release",
    body: "Delivery is checked against the agreed conditions. Acceptance, warranty activation or payment-release status is recorded through an attributable receipt.",
  },
];

const consequences = [
  "the supplier keeps custody of sensitive evidence;",
  "the buyer agent operates only within a defined mandate;",
  "technical and financial authority remain visible;",
  "exceptions are routed to accountable people;",
  "the outcome can be proven without centralising the underlying files.",
];

export const JourneyGovernedAgenticProcurement = () => {
  usePageMeta({
    title: "Governed Agentic Procurement — Cierge Labs",
    description:
      "An illustrative Cierge Labs reference journey showing how KATLAS complements machine-readable commerce protocols when an AI-assisted purchase has financial, technical, safety, warranty, sustainability or operational consequences.",
    ogTitle: "Governed Agentic Procurement — Cierge Labs",
    ogDescription:
      "An illustrative KATLAS reference architecture for AI-assisted procurement of circular assets. Custody, authority, evidence, receipts.",
    ogUrl: "https://labs.katlastechnology.com/journeys/governed-agentic-procurement",
  });

  return (
    <div
      data-testid="journey-governed-procurement"
      className="bg-white"
    >
      {/* HERO — dark navy, KATLAS-aligned. No 'KATLAS × UCP' framing. */}
      <section
        data-testid="journey-hero"
        className="relative overflow-hidden border-b border-border text-white"
        style={{
          background:
            "linear-gradient(180deg, #061A2F 0%, #0A2540 55%, #0A2540 100%)",
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "100% 96px",
          }}
        />
        <div className="relative mx-auto max-w-[1080px] px-6 py-20 md:py-24 lg:px-10 lg:py-28">
          <p
            data-testid="journey-eyebrow"
            className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#7FB2FF]"
          >
            <Link
              to="/journeys"
              data-testid="journey-back-to-index"
              className="border-b border-transparent transition-colors hover:border-[#7FB2FF]/60"
            >
              Cierge Labs · Journey storybook
            </Link>
          </p>

          <div
            data-testid="journey-status-badge"
            className="mt-4 inline-flex items-center gap-2 border border-[#C19A4A]/40 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C19A4A]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C19A4A]" />
            <span>Illustrative reference architecture</span>
          </div>

          <h1
            data-testid="journey-headline"
            className="mt-6 font-serif text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-[68px]"
          >
            Governed Agentic Procurement.
          </h1>
          <p
            data-testid="journey-subhead"
            className="mt-4 font-serif text-xl font-light italic text-white/70 sm:text-2xl"
          >
            An illustrative circular-asset reference journey.
          </p>

          <p
            data-testid="journey-status-copy"
            className="mt-8 max-w-3xl text-sm leading-relaxed text-white/80 sm:text-base"
          >
            This is an illustrative governed-procurement journey. It does not
            represent a live merchant integration, live protocol implementation
            or live KATLAS-signed transaction.
          </p>

          <p
            data-testid="journey-disclaimer"
            className="mt-6 max-w-3xl border-l-2 border-white/20 pl-5 text-sm leading-relaxed text-white/70"
          >
            This independent reference journey draws on public descriptions of
            machine-readable commerce protocols, including the Universal
            Commerce Protocol (UCP). It is not a Shopify, Google, UCP or other
            third-party partnership, integration, certification, endorsement or
            live protocol implementation.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
            <span data-testid="tag-sector">› Primary sector · Supply Chain</span>
            <span>› Circular assets · Manufacturing · Procurement</span>
            <span>› Cross-sector: secondary</span>
          </div>
        </div>
      </section>

      {/* CORE PROPOSITION */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#0A2540]/70">
            Core proposition
          </p>
          <h2 className="mt-3 max-w-3xl font-serif text-3xl font-light leading-tight tracking-tight text-[#0A2540] sm:text-4xl">
            Open commerce protocols can help an agent discover products,
            compare offers and complete a transaction. KATLAS governs the
            consequential decision around that transaction.
          </h2>
          <p className="mt-6 border-l-2 border-[#0A2540]/40 pl-5 font-mono text-sm uppercase tracking-[0.12em] text-[#0A2540]">
            Custody controls the evidence. Authority governs the action.
            Receipts prove what happened.
          </p>
        </div>
      </section>

      {/* SCENARIO */}
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Scenario
              </p>
              <h2 className="mt-3 font-serif text-3xl font-light leading-tight tracking-tight">
                A refurbished controller for a planned production-line repair.
              </h2>
            </div>
            <div className="space-y-6 text-base leading-relaxed text-foreground/90 md:col-span-8">
              <p>
                A manufacturer needs a refurbished robotic controller for a
                planned production-line repair. A procurement agent discovers
                compatible circular assets through a machine-readable merchant
                or marketplace interface. It can compare availability, price,
                delivery timing and standard product information.
              </p>
              <p className="font-medium text-[#0A2540]">
                But the agent cannot simply buy.
              </p>
              <p>The organisation must establish:</p>
              <ul className="list-none space-y-3 border-l border-border pl-5 text-sm leading-relaxed text-foreground/80">
                <li>· Is this asset technically suitable for the intended equipment?</li>
                <li>· Is the supplier approved for this asset class?</li>
                <li>· Does the buyer agent have authority for this project, asset category and spend limit?</li>
                <li>· What repair, warranty, provenance and sustainability evidence is required?</li>
                <li>· Which evidence may be verified without being copied into a central marketplace?</li>
                <li>· Who may approve an exception?</li>
                <li>· What proof remains when the asset is ordered, delivered and accepted?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ARCHITECTURE — the only section that names UCP */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Architecture
          </p>
          <h2 className="mt-3 max-w-3xl font-serif text-3xl font-light leading-tight tracking-tight sm:text-4xl">
            Four layers, intentionally separated.
          </h2>

          <ol className="mt-10 grid gap-px bg-border md:grid-cols-2">
            {architectureLayers.map((l) => (
              <li
                key={l.n}
                data-testid={`arch-layer-${l.n}`}
                className="bg-white p-7"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0A2540]">
                  Layer {l.n}
                </p>
                <h3 className="mt-3 font-serif text-2xl font-light tracking-tight text-foreground">
                  {l.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {l.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SIX-STAGE JOURNEY */}
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Six-stage journey
          </p>
          <h2 className="mt-3 max-w-3xl font-serif text-3xl font-light leading-tight tracking-tight sm:text-4xl">
            From discovery to attributable acceptance.
          </h2>

          <ol className="mt-10 space-y-px bg-border">
            {journey.map((s) => (
              <li
                key={s.n}
                data-testid={`journey-stage-${s.n}`}
                className="grid grid-cols-[88px_1fr] gap-6 bg-white p-7"
              >
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Step {s.n}
                </span>
                <div>
                  <p className="font-serif text-xl font-light tracking-tight text-foreground">
                    {s.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHAT THIS CHANGES */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                What this changes
              </p>
              <h2 className="mt-3 font-serif text-3xl font-light leading-tight tracking-tight">
                Without governed coordination, agentic procurement risks
                becoming faster purchasing on top of old uncertainty.
              </h2>
            </div>
            <div className="md:col-span-8">
              <p className="text-base leading-relaxed text-foreground/90">
                With KATLAS:
              </p>
              <ul className="mt-4 space-y-3 border-l-2 border-[#0A2540]/40 pl-5 text-base leading-relaxed text-foreground/85">
                {consequences.map((c) => (
                  <li key={c}>· {c}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING LINE + CTAS */}
      <section
        data-testid="journey-closing"
        className="relative overflow-hidden border-b border-border bg-[#061A2F] text-white"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "100% 96px",
          }}
        />
        <div className="relative mx-auto max-w-[1080px] px-6 py-20 md:py-24 lg:px-10">
          <p className="max-w-3xl font-serif text-3xl font-light leading-[1.2] tracking-tight text-white sm:text-4xl">
            Machine-readable commerce tells an agent what it can buy.
          </p>
          <p className="mt-3 max-w-3xl font-serif text-3xl font-light leading-[1.2] tracking-tight text-[#C19A4A] sm:text-4xl">
            KATLAS governs what it is entitled to procure, under whose
            authority, with what evidence and with what proof.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              data-testid="journey-cta-primary"
              className="rounded-none bg-white px-7 font-mono text-xs uppercase tracking-[0.18em] text-[#0A2540] hover:bg-[#E6F0FF]"
            >
              <Link to="/explore?sector=supply-chain">
                Shape your governed procurement use case
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="journey-cta-secondary"
              className="rounded-none border-white/30 bg-transparent px-7 font-mono text-xs uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 hover:text-white"
            >
              <Link to="/sandbox-request?intent=walkthrough">
                Request a walkthrough →
              </Link>
            </Button>
          </div>

          <p className="mt-12 max-w-3xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-white/55">
            Illustrative reference only. No live merchant, marketplace,
            protocol integration, third-party endorsement or live
            KATLAS-signed transaction is implied by this page.
          </p>
        </div>
      </section>
    </div>
  );
};

export default JourneyGovernedAgenticProcurement;
